import { useEffect, useMemo, useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import {
  useActivePromptQuery,
  useCreateDraftMutation,
  usePromptHistoryQuery,
  usePublishVersionMutation,
  useRunPreviewMutation,
} from "@/lib/staff/prompt-mutations";
import {
  PREVIEW_METHOD_LABELS,
  SAMPLE_BOOKS,
  type MargauxPreviewMethod,
  type MargauxPromptVersion,
} from "@/lib/staff/prompt-types";
import styles from "./staff-margaux-prompt.module.scss";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StaffMargauxPrompt() {
  const activeQuery = useActivePromptQuery();
  const historyQuery = usePromptHistoryQuery();
  const createDraft = useCreateDraftMutation();
  const publishVersion = usePublishVersionMutation();
  const runPreview = useRunPreviewMutation();

  const active = activeQuery.data;

  const [draftBody, setDraftBody] = useState<string>("");
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [method, setMethod] = useState<MargauxPreviewMethod>("generateIntro");
  const [bookId, setBookId] = useState<string>(SAMPLE_BOOKS[0].id);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewOutput, setPreviewOutput] = useState<string | null>(null);

  // Initialize the editor with the active body once it loads.
  useEffect(() => {
    if (active && draftBody === "" && editingVersionId === null) {
      setDraftBody(active.body);
      setEditingVersionId(active.id);
    }
  }, [active, draftBody, editingVersionId]);

  const isDirty = useMemo(
    () => active != null && draftBody.trim() !== active.body.trim(),
    [active, draftBody],
  );

  const charCount = draftBody.length;

  const selectedBook = useMemo(
    () => SAMPLE_BOOKS.find((b) => b.id === bookId)!.book,
    [bookId],
  );

  function handleDiscard() {
    if (!active) return;
    setDraftBody(active.body);
    setEditingVersionId(active.id);
  }

  async function handleSaveDraft() {
    const body = draftBody.trim();
    if (!body) return;
    try {
      const draft = await createDraft.mutateAsync({ body });
      setEditingVersionId(draft.id);
    } catch {
      /* swallow — error surfaced via mutation state */
    }
  }

  async function handlePublishCurrentEditor() {
    const body = draftBody.trim();
    if (!body) return;
    if (
      !confirm("Publish this prompt? Margaux will use it on her next call.")
    ) {
      return;
    }
    try {
      // Save as draft first (if dirty), then publish that draft.
      let targetId = editingVersionId;
      if (isDirty || editingVersionId === active?.id) {
        const draft = await createDraft.mutateAsync({ body });
        targetId = draft.id;
      }
      if (!targetId) return;
      await publishVersion.mutateAsync({ id: targetId });
      setEditingVersionId(targetId);
    } catch {
      /* surfaced below */
    }
  }

  async function handleRollback(version: MargauxPromptVersion) {
    if (
      !confirm(
        `Roll back to the version from ${formatDate(version.createdAt)}? Margaux will use it on her next call.`,
      )
    ) {
      return;
    }
    try {
      await publishVersion.mutateAsync({ id: version.id });
      setDraftBody(version.body);
      setEditingVersionId(version.id);
      setShowHistory(false);
    } catch {
      /* surfaced below */
    }
  }

  function handleViewVersion(version: MargauxPromptVersion) {
    setDraftBody(version.body);
    setEditingVersionId(version.id);
    setShowHistory(false);
  }

  async function handleRunPreview() {
    setPreviewError(null);
    setPreviewOutput(null);
    const body = draftBody.trim();
    if (!body) {
      setPreviewError("Editor is empty");
      return;
    }
    try {
      const result = await runPreview.mutateAsync({
        body,
        method,
        book: selectedBook,
      });
      setPreviewOutput(result.output ?? "(no output)");
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Preview failed");
    }
  }

  const headSubtitle = active
    ? `${active.id} · published ${active.publishedAt ? formatDate(active.publishedAt) : "—"}`
    : "Loading active prompt…";

  return (
    <main className={styles.shell}>
      <header className={styles.head}>
        <div className={styles.headLeft}>
          <span className={styles.headEyebrow}>
            Platform-wide system prompt
          </span>
          <h1 className={styles.headTitle}>Margaux prompt</h1>
          <span className={styles.headSub}>{headSubtitle}</span>
        </div>
        <div className={styles.actions}>
          {isDirty ? (
            <span className={styles.statusChip}>Unpublished changes</span>
          ) : null}
          <button
            type="button"
            className={styles.btn}
            onClick={() => setShowHistory(true)}
          >
            View history
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={handleDiscard}
            disabled={!isDirty}
          >
            Discard changes
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={handleSaveDraft}
            disabled={
              !isDirty || createDraft.isPending || draftBody.trim().length === 0
            }
          >
            {createDraft.isPending ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handlePublishCurrentEditor}
            disabled={publishVersion.isPending || draftBody.trim().length === 0}
          >
            {publishVersion.isPending ? "Publishing…" : "Publish"}
          </button>
        </div>
      </header>

      {publishVersion.isError ? (
        <div className={styles.errorBanner} role="alert">
          Publish failed:{" "}
          {(publishVersion.error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}
      {createDraft.isError ? (
        <div className={styles.errorBanner} role="alert">
          Save failed:{" "}
          {(createDraft.error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      <div className={styles.cols}>
        <section className={`${styles.col} ${styles.colLeft}`}>
          <header className={styles.colHead}>
            <h2 className={styles.colTitle}>Editor</h2>
            <span className={styles.colMeta}>{charCount} chars</span>
          </header>
          <div className={styles.editorWrap}>
            <textarea
              className={styles.editor}
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              spellCheck={false}
              aria-label="Margaux system prompt body"
              disabled={activeQuery.isLoading}
            />
          </div>
        </section>

        <section className={styles.col}>
          <header className={styles.colHead}>
            <h2 className={styles.colTitle}>Live preview</h2>
            <span className={styles.colMeta}>
              {runPreview.isPending ? "Running…" : "gpt-4o-mini"}
            </span>
          </header>
          <div className={styles.previewBox}>
            <div className={styles.previewControls}>
              <div className={styles.previewControlsRow}>
                <span className={styles.previewControlsLabel}>Method</span>
                <select
                  className={styles.select}
                  value={method}
                  onChange={(e) =>
                    setMethod(e.target.value as MargauxPreviewMethod)
                  }
                  aria-label="Preview method"
                >
                  {(
                    Object.keys(PREVIEW_METHOD_LABELS) as MargauxPreviewMethod[]
                  ).map((m) => (
                    <option key={m} value={m}>
                      {PREVIEW_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.previewControlsRow}>
                <span className={styles.previewControlsLabel}>Book</span>
                <select
                  className={styles.select}
                  value={bookId}
                  onChange={(e) => setBookId(e.target.value)}
                  aria-label="Sample book"
                >
                  {SAMPLE_BOOKS.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.book.title} — {b.book.author}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.previewControlsRow}>
                <span className={styles.previewControlsLabel}>&nbsp;</span>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleRunPreview}
                  disabled={runPreview.isPending}
                  style={{ flex: 1 }}
                >
                  {runPreview.isPending ? "Running preview…" : "Run preview"}
                </button>
              </div>
            </div>

            {previewError ? (
              <div className={styles.errorBanner} role="alert">
                {previewError}
              </div>
            ) : null}

            {previewOutput ? (
              <div className={styles.previewBubble}>
                <div className={styles.bubbleAuthor}>Margaux</div>
                {previewOutput}
              </div>
            ) : !previewError ? (
              <div className={styles.previewEmpty}>
                Pick a method and book, then run a preview to see Margaux's
                output with this prompt.
              </div>
            ) : null}
          </div>
        </section>
      </div>

      {showHistory ? (
        <HistoryDrawer
          versions={historyQuery.data ?? []}
          activeId={active?.id ?? null}
          isLoading={historyQuery.isLoading}
          onClose={() => setShowHistory(false)}
          onView={handleViewVersion}
          onRollback={handleRollback}
          isRolling={publishVersion.isPending}
        />
      ) : null}
    </main>
  );
}

interface HistoryDrawerProps {
  versions: MargauxPromptVersion[];
  activeId: string | null;
  isLoading: boolean;
  isRolling: boolean;
  onClose: () => void;
  onView: (v: MargauxPromptVersion) => void;
  onRollback: (v: MargauxPromptVersion) => void;
}

function HistoryDrawer({
  versions,
  activeId,
  isLoading,
  isRolling,
  onClose,
  onView,
  onRollback,
}: HistoryDrawerProps) {
  return (
    <div
      className={styles.historyOverlay}
      role="dialog"
      aria-label="Prompt version history"
      onClick={onClose}
    >
      <aside
        className={styles.historyDrawer}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.historyHead}>
          <h2 className={styles.historyTitle}>Version history</h2>
          <button
            type="button"
            className={styles.btn}
            onClick={onClose}
            aria-label="Close history"
          >
            Close
          </button>
        </header>
        {isLoading ? (
          <div style={{ padding: "16px 20px" }}>Loading history…</div>
        ) : versions.length === 0 ? (
          <div style={{ padding: "16px 20px" }}>No versions yet.</div>
        ) : (
          <ul className={styles.historyList}>
            {versions.map((v) => {
              const isActive = v.id === activeId;
              return (
                <li key={v.id} className={styles.historyItem}>
                  <div className={styles.historyMeta}>
                    <span>{v.id}</span>
                    <span>·</span>
                    <span>{v.status}</span>
                    <span>·</span>
                    <span>{formatDate(v.createdAt)}</span>
                  </div>
                  <div className={styles.historyBody}>
                    {v.body.length > 200 ? `${v.body.slice(0, 200)}…` : v.body}
                  </div>
                  <div className={styles.historyActions}>
                    <button
                      type="button"
                      className={styles.btn}
                      onClick={() => onView(v)}
                    >
                      View
                    </button>
                    {!isActive ? (
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => onRollback(v)}
                        disabled={isRolling}
                      >
                        {isRolling ? "Publishing…" : "Roll back to this"}
                      </button>
                    ) : (
                      <span className={styles.statusChip}>Active</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
    </div>
  );
}

export const staffMargauxPromptRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/margaux/prompt",
  component: StaffMargauxPrompt,
});
